import React, { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Colors, Checkbox, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { MdMailOutline } from 'react-icons/md';
import { FaUser } from 'react-icons/fa';
import { FiLock, FiRepeat } from 'react-icons/fi';

// data
import { light, dark } from 'data/themes';

// components
import BrandHeader from './PaywallElements/BrandHeader';
import Header from './PaywallElements/Header';
import Footer from './PaywallElements/Footer';
import {
  PaywallContainer,
  SectionContainer,
  StyledInput,
  FormContainer,
  ButtonHolder,
  Button,
  InputContainer,
} from './StyledElements';

const getIconStyles = () => {
  return css<{ color: string }>`
    position: absolute;
    top: 15px;
    left: 0;
    font-size: ${({ theme }) => theme.font.sizes.large};
    color: ${({ color }) => ifProp('color', color, Colors.gray)};
  `;
};

const StyledIcon = styled(MdMailOutline)<{ color: string }>`
  ${getIconStyles}
`;

const StyledInputContainer = styled(InputContainer)`
  margin: 1rem 0;
`;

interface CheckboxContainerProps {
  borderColor: string;
  isDarkModeEnabled: boolean;
}

const CheckboxContainer = styled.div<CheckboxContainerProps>`
  display: flex;
  margin: 2rem auto 1rem;
  color: ${({ isDarkModeEnabled }) => (isDarkModeEnabled ? dark.palette.text.main : light.palette.text.main)};

  a {
    margin-left: 0.3rem;
    line-height: 1.2rem;
    color: ${({ borderColor }) => borderColor};
    font-weight: ${({ theme }) => theme.font.weights.semiBold};
  }

  input + label {
    cursor: default;
  }

  label {
    color: ${({ isDarkModeEnabled }) => (isDarkModeEnabled ? dark.palette.text.main : light.palette.text.main)};
  }

  input:not(:checked) + label::before,
  input:not(:checked) + label:hover::before {
    border: 1px solid ${({ borderColor }) => borderColor};
    background-color: ${Colors.white};
  }
`;

const FildInput = styled(StyledInput)`
  line-height: 20px;
  padding-left: 25px;
`;

interface Props {
  branding?: any;
  hideLogo?: boolean;
}

const PlainRegisterScreen: FunctionComponent<Props> = ({
  branding: {
    paywall_secondary_color: paywallSecondaryColor,
    paywall_primary_color: paywallPrimaryColor,
    paywall_buttons_bg_color: paywallButtonsBgColor,
    paywall_brand_logo: paywallBrandLogo,
    terms_url: termsUrl,
    dark_mode: isDarkModeEnabled,
  },
  hideLogo,
}: Props) => {
  return (
    <AnalyticsComponent>
      {({ pages, tracker, merchantId, ip }) => (
        <PaywallContainer isDarkModeEnabled={isDarkModeEnabled}>
          <BrandHeader backgroundColor={paywallSecondaryColor} brandLogo={paywallBrandLogo} hideLogo={hideLogo!} />
          <Header borderColor={paywallPrimaryColor} isRegister isDarkModeEnabled={isDarkModeEnabled} />
          <SectionContainer isDarkModeEnabled={isDarkModeEnabled}>
            <FormContainer>
              <StyledInputContainer>
                <StyledIcon as={MdMailOutline} color={paywallPrimaryColor} />
                <FildInput borderColor={paywallPrimaryColor} type="text" placeholder="Email*" readOnly />
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledIcon as={FaUser} color={paywallPrimaryColor} />
                <FildInput borderColor={paywallPrimaryColor} type="text" placeholder="Full name*" readOnly />
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledIcon as={FiLock} color={paywallPrimaryColor} />
                <FildInput borderColor={paywallPrimaryColor} type="text" placeholder="Password*" readOnly />
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledIcon as={FiRepeat} color={paywallPrimaryColor} />
                <FildInput borderColor={paywallPrimaryColor} type="text" placeholder="Repeat password*" readOnly />
              </StyledInputContainer>
              <CheckboxContainer borderColor={paywallPrimaryColor} isDarkModeEnabled={isDarkModeEnabled}>
                <Checkbox id="ipx-register" name="terms" label="I agree to the" onChange={() => {}} checked={false} />
                <a
                  onClick={() => {
                    tracker.track({
                      event: 'click',
                      type: 'link',
                      tag: 'link_terms',
                      pages,
                      merchantId,
                      ip,
                    });
                  }}
                  href={termsUrl || 'https://inplayer.com/terms/'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms
                </a>
                *
              </CheckboxContainer>
              <ButtonHolder>
                <Button buttonBgColor={paywallButtonsBgColor}>Register</Button>
              </ButtonHolder>
            </FormContainer>
          </SectionContainer>
          <Footer backgroundColor={paywallPrimaryColor} isDarkModeEnabled={isDarkModeEnabled} />
        </PaywallContainer>
      )}
    </AnalyticsComponent>
  );
};

export default PlainRegisterScreen;
